import {
  type APIOrganizationUser,
  type ArdoqId,
  type FilterInterfaceFilter,
  type FormattingFilter,
  type GroupingRule,
  ViewIds,
} from '@ardoq/api-types';
import { combineLatest, Observable } from 'rxjs';
import { debounceTime, filter, map, tap, withLatestFrom } from 'rxjs/operators';
import {
  BlocksViewModelProperties,
  BlocksViewRenderingData,
  BlocksViewSettings,
  BlocksViewSourceDataShape,
} from '../types';
import {
  startViewLoadTracking,
  recordViewModelReady,
  DEFAULT_EXPECTED_DURATION,
} from 'tracking/durationTracking/viewPerformance';
import {
  action$,
  combineReducers,
  dispatchAction,
  streamReducer,
  withNamespace,
} from '@ardoq/rxbeach';
import { viewSettingsConsts } from '@ardoq/view-settings';
import { buildBlocksViewRenderingData } from './buildBlocksViewProperties';
import {
  buildBlocksViewSourceData,
  getEmptyBlocksViewSourceData,
} from './buildBlocksViewSourceData';
import { HasViewInstanceId } from '@ardoq/graph';
import { updateViewEmptyStateData } from 'appLayout/ardoqStudio/emptyState/actions';
import {
  GenericInstanceViewSettings,
  ViewpointModeInstanceViewSettings,
} from 'views/environmentals/viewSettings/types';
import { DEFAULT_VIEWPOINT_MODE_SETTINGS } from 'views/environmentals/viewSettings/defaultStreamState';
import { getFilteredViewSettingsPlain$ } from 'views/filteredViewSettings$';
import { getPerformanceTrackingData } from './util';
import { isAppliedRecipeAcknowledgement } from '../layout/arrangementLayout';
import {
  allActiveOrgUsers$,
  globalViewModelResetSource$,
  orgUsersById$,
} from 'tabview/relationshipDiagrams/shared/sharedGlobalStreams';
import { componentsChanged$ } from 'streams/components/components$';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
import { readViewComponentIds } from 'streams/viewComponentIds$';
import {
  getComponentIdsFromViewModel,
  getRelationshipsViewLegendContents,
} from 'tabview/relationshipDiagrams/utils';
import { viewIdToUserFriendlyName } from 'views/metaInfoConsts';
import { setStateProperty } from '@ardoq/common-helpers';
import { scopeDiff$ } from 'scope/scopeDiff$';
import { difference, noop } from 'lodash';
import { ViewpointRelationshipDiagramResetStreamShape } from 'tabview/relationshipsDiagramsViewModel/types';
import { handleViewpointComponentsUpdate } from 'tabview/relationshipDiagrams/viewModel$/handleViewpointComponentUpdate';
import { GraphViewLegendProps } from '@ardoq/view-legend';
import { type I18nLocale } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { locale$ } from 'streams/locale$';
import { groupingRules$ } from 'streams/grouping/groupingRules$';
import { formattingAndLabelRules$ } from 'streams/filters/formattingAndLabelRules$';
import { groupingRuleInterface } from 'modelInterface/groupingRules/groupingRuleInterface';
import type {
  HasDataWorkToken,
  ViewStatusReporter,
} from 'tabview/activeViewsStatus/types';
import {
  reportDataWorkStarted,
  withDataWork,
} from 'tabview/activeViewsStatus/viewStatusReporter';

const VIEW_ID = ViewIds.BLOCKS;

const VIEW_SETTINGS_TO_IGNORE = [
  'layoutData',
  'arrangements',
  'linksAggregation',
  viewSettingsConsts.IS_LEGEND_ACTIVE,
] as const satisfies (keyof BlocksViewSettings)[];

export const isSourceDataSettingsChange = (
  changedSettings: [PropertyKey, unknown][]
): boolean =>
  changedSettings.some(
    ([key]) =>
      !(VIEW_SETTINGS_TO_IGNORE as readonly PropertyKey[]).includes(key)
  ) && !isAppliedRecipeAcknowledgement(changedSettings);

type BlocksViewResetStreamShape =
  ViewpointRelationshipDiagramResetStreamShape<BlocksViewSettings> & {
    groupingRules: GroupingRule[];
    formattingAndLabelRules: (FilterInterfaceFilter | FormattingFilter)[];
    orgUsersById: Record<ArdoqId, APIOrganizationUser>;
  };

const resetReducer = (
  previousState: BlocksViewSourceDataShape,
  {
    context,
    loadedGraph,
    scenario: [
      loadedScenarioData,
      scenarioRelatedComponents,
      { showScopeRelated },
    ],
    viewSettings,
    users,
    i18nLocale,
    orgUsersById,
    groupingRules,
    formattingAndLabelRules,
  }: BlocksViewResetStreamShape
): BlocksViewSourceDataShape => {
  const performanceTrackingId = startViewLoadTracking(
    viewIdToUserFriendlyName[VIEW_ID],
    DEFAULT_EXPECTED_DURATION
  );

  return buildBlocksViewSourceData(previousState, {
    context,
    loadedGraph,
    loadedScenarioData,
    scenarioRelatedComponents,
    showScopeRelated,
    viewSettings,
    users,
    i18nLocale,
    orgUsersById,
    groupingRules,
    formattingAndLabelRules,
    performanceTrackingId,
  });
};

export const toBlocksViewModelProperties = (
  renderingData: BlocksViewRenderingData,
  viewSettings: BlocksViewSettings,
  i18nLocale: I18nLocale
): BlocksViewModelProperties => {
  const additionalRows = getRelationshipsViewLegendContents({
    componentTypes: renderingData.componentTypes,
    referenceTypes: renderingData.referenceModelTypes,
    selectedComponentTypes: [],
    selectedReferenceTypes: [],
    selectedConditionalFormattingIds: [],
    hasCollapsedNodes: false,
    hasNonComponentNodes: false,
    hasReferenceParents: false,
    isUserDefinedGrouping: false,
    showComponentSwatches: true,
    activeConditionalFormattingForLegend:
      renderingData.activeConditionalFormattingForLegend,
    showComponentShapes: false,
    showReferencesAsLines: true,
    selectableItems: false,
    isNewConditionalFormatting: true,
    i18nLocale,
  });
  const legendProps: GraphViewLegendProps = {
    additionalRows,
    activeDiffMode: null,
    itemClickTrackingFunction: noop,
  };
  return {
    ...renderingData,
    viewSettings,
    i18nLocale,
    uiPropsAndCallbacks: {
      legendProps,
    },
  };
};

type ToPropsArgs = [
  [
    BlocksViewRenderingData & HasDataWorkToken,
    GenericInstanceViewSettings<BlocksViewSettings>,
    I18nLocale,
  ],
  BlocksViewSettings,
];
const toProps = ([
  [{ dataWorkToken, ...renderingData }, _ignoredSettings, i18nLocale],
  viewSettings,
]: ToPropsArgs): BlocksViewModelProperties & HasDataWorkToken => ({
  ...toBlocksViewModelProperties(renderingData, viewSettings, i18nLocale),
  dataWorkToken,
});

export type GetBlocksViewModelArgs = HasViewInstanceId & {
  viewSettingsWithChanges$: Observable<
    ViewpointModeInstanceViewSettings<ViewIds.BLOCKS>
  >;
  viewStatusReporter: ViewStatusReporter;
};
const getViewModel$ = ({
  viewInstanceId,
  viewSettingsWithChanges$,
  viewStatusReporter,
}: GetBlocksViewModelArgs): Observable<
  BlocksViewModelProperties & HasDataWorkToken
> => {
  const filteredViewSettings$ = viewSettingsWithChanges$.pipe(
    filter(
      ({ changedSettings }, index) =>
        index === 0 ||
        changedSettings.length === 0 ||
        isSourceDataSettingsChange(changedSettings)
    )
  );
  const unfilteredSettings$: Observable<BlocksViewSettings> =
    viewSettingsWithChanges$.pipe(
      map(({ currentSettings }) => currentSettings)
    );
  const ignoredSettings$ = getFilteredViewSettingsPlain$(
    viewSettingsWithChanges$,
    difference(
      Object.keys(
        DEFAULT_VIEWPOINT_MODE_SETTINGS[ViewIds.BLOCKS]
      ) as (keyof BlocksViewSettings)[],
      VIEW_SETTINGS_TO_IGNORE
    )
  );

  const sourceDataReset$: Observable<BlocksViewResetStreamShape> =
    combineLatest([
      globalViewModelResetSource$,
      filteredViewSettings$,
      allActiveOrgUsers$,
      orgUsersById$,
      groupingRules$.pipe(
        map(({ groupingRules }) =>
          groupingRules.map(groupingRuleInterface.toInterfaceType)
        )
      ),
      formattingAndLabelRules$,
    ]).pipe(
      withLatestFrom(unfilteredSettings$),
      map(
        ([
          [
            globalAppState,
            _filteredSettings,
            users,
            orgUsersById,
            groupingRules,
            { rules },
          ],
          viewSettings,
        ]) => ({
          ...globalAppState,
          viewSettings,
          users,
          orgUsersById,
          scopeDataParams: null,
          groupingRules,
          formattingAndLabelRules: rules,
        })
      ),
      reportDataWorkStarted(viewStatusReporter, 'reset')
    );

  const viewModelSourceData$: Observable<
    BlocksViewSourceDataShape & HasDataWorkToken
  > = action$.pipe(
    withNamespace(viewInstanceId),
    combineReducers(getEmptyBlocksViewSourceData(viewInstanceId), [
      streamReducer(sourceDataReset$, resetReducer),
      streamReducer(componentsChanged$, handleViewpointComponentsUpdate),
      streamReducer(scopeDiff$, setStateProperty('scopeDiffState')),
    ]),
    debounceTime(2 * DEFAULT_DEBOUNCE_TIME),
    tap(viewModel => {
      dispatchAction(
        readViewComponentIds({
          [VIEW_ID]: getComponentIdsFromViewModel(viewModel),
        })
      );
    }),
    withDataWork(viewStatusReporter, 'source')
  );

  const renderingData$ = viewModelSourceData$.pipe(
    withLatestFrom(unfilteredSettings$, locale$),
    map(([{ dataWorkToken, ...sourceData }, unfilteredSettings, locale]) => ({
      ...buildBlocksViewRenderingData(
        { ...sourceData, viewSettings: unfilteredSettings },
        locale
      ),
      dataWorkToken,
    })),
    tap(viewProperties => {
      dispatchAction(
        updateViewEmptyStateData({
          viewEmptyStateData: {
            isViewEmpty: viewProperties.isViewEmpty,
          },
          viewId: VIEW_ID,
        })
      );
      if (viewProperties.performanceTrackingId) {
        const metrics = getPerformanceTrackingData(viewProperties);
        recordViewModelReady(viewProperties.performanceTrackingId, metrics);
      }
    })
  );

  return combineLatest([renderingData$, ignoredSettings$, i18nLocale$]).pipe(
    withLatestFrom(unfilteredSettings$),
    map(toProps)
  );
};

export { getViewModel$ };
